import { FC, ReactNode, useState } from 'react'
import PerSpacer from '../Spacer'
import PerFlexRow from '../FlexRow'
import PerText from '../Text'
import { SmartAvatar } from '../../../modules/avatar/SmartAvatar'
import { useTranslation } from 'react-i18next'
import { computeFullname } from '../../../shared/nameDisplayPreferencesUtil'
import { NameDisplayPreference } from '../../../graphql/enum/name-display-preference.enum'
import { highlightSearchTerm } from '../Search/Search'

interface Props {
  employeeId?: number
  firstName?: string
  lastName?: string
  fullName?: string
  infoText?: ReactNode | string
  unknown?: boolean
  nameType?: 'fullName' | 'firstName' | 'you'
  nameDisplayPreference: NameDisplayPreference
  linkToProfile?: LinkToProfile
  tooltip?: string
  wordBreak?: boolean
  searchTerm?: string
}

export type LinkToProfile = {
  resigned: boolean
}

export const PerAvatarPlus: FC<Props> = ({
  employeeId,
  firstName,
  lastName,
  fullName,
  infoText,
  unknown = false,
  nameType = 'fullName',
  nameDisplayPreference,
  linkToProfile,
  tooltip,
  wordBreak = false,
  searchTerm,
}) => {
  const [t] = useTranslation()
  const [hoveredEmployeeName, setHoveredEmployeeName] = useState(false)

  const url = linkToProfile ? (linkToProfile.resigned ? `/profile/${employeeId}/resigned` : `/profile/${employeeId}`) : undefined

  const renderName = () => {
    if (unknown) {
      return t('user.unknown.name')
    } else if (!!fullName) {
      return fullName
    } else if (nameType === 'fullName') {
      return computeFullname(nameDisplayPreference, firstName, lastName)
    } else if (nameType === 'firstName') {
      return firstName
    } else if (nameType === 'you') {
      return t('global.you')
    } else {
      return undefined
    }
  }

  const innerText = (): JSX.Element => {
    if (searchTerm && !hoveredEmployeeName) {
      return (
        <div onMouseOver={() => setHoveredEmployeeName(true)}>
          <PerText fontWeight="bold" tooltip={tooltip} linkToProfileSearch={true}>
            {highlightSearchTerm(renderName(), searchTerm)}
          </PerText>
        </div>
      )
    } else if (searchTerm && hoveredEmployeeName) {
      return (
        <div onMouseOut={() => setHoveredEmployeeName(false)}>
          <PerText fontWeight="bold" color={unknown ? 'secondary' : undefined} tooltip={tooltip} linkToProfile={linkToProfile}>
            {renderName()}
          </PerText>
        </div>
      )
    }
    return (
      <PerText
        fontWeight="bold"
        color={unknown ? 'secondary' : undefined}
        tooltip={tooltip}
        linkToProfile={linkToProfile}
        wordBreak={wordBreak}
        className="truncate-text"
      >
        {renderName()}
      </PerText>
    )
  }

  const avatar = (): JSX.Element => {
    return (
      <SmartAvatar employeeId={employeeId ? employeeId : -1} size="small" firstName={firstName} lastName={lastName} unknown={unknown} />
    )
  }

  return (
    <PerFlexRow align="middle" noWrap>
      {linkToProfile ? (
        <a href={url} target="_blank" rel="noopener noreferrer">
          {avatar()}
        </a>
      ) : (
        avatar()
      )}
      <PerSpacer ml="xs" className="overflow--hidden">
        {linkToProfile ? (
          <a href={url} target="_blank" rel="noopener noreferrer">
            {innerText()}
          </a>
        ) : (
          innerText()
        )}
        {!unknown && nameType !== 'you' && infoText && (
          <PerText color="secondary" fontSize="xs" wordBreak={wordBreak}>
            {infoText}
          </PerText>
        )}
      </PerSpacer>
    </PerFlexRow>
  )
}
